import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Heading from '@atoms/Heading';
import HeadingSize from '@atoms/Heading/sizes';
import ToggleButtons from '@molecules/ToggleButtons';
import ButtonType from '@atoms/Button/type';
import { ButtonProps } from '@atoms/Button';
import Info from '@atoms/Info';
import GrayContainer from '@atoms/GrayContainer';

import {
  PalletType, UnitDimension,
} from 'enums/calculator';

import { ReactComponent as PalletIcon } from 'images/icons/pallet.svg';
import useCalculatorState from 'hooks/useCalculatorState';
import NextPrevButtons from '@molecules/NextPrevButtons';
import useBreakpoint from 'hooks/useBreakpoint';
import Input from '@molecules/Input';
import Selector from '@molecules/Selector';
import { PALLET_MAX_HEIGHT_INCHES } from 'config/calculator';

import styles from './styles';

const Step2 = () => {
  const { t } = useTranslation();
  const {
    palletData,

    isStep2Valid,

    setPalletData,
    setPalletUnit,
    setPalletWidth,
    setPalletLength,
    setPalletHeight,
  } = useCalculatorState();

  const {
    isMobile,
  } = useBreakpoint();

  const {
    widthError,
    lengthError,
    heightError,
  } = palletData || {};

  const palletTypes: ButtonProps[] = [
    {
      type: ButtonType.OUTLINE,
      children: PalletType.PALLET_40x48,
      active: palletData.type === PalletType.PALLET_40x48,
      legend: 'Pallet',
    },
    {
      type: ButtonType.OUTLINE,
      children: PalletType.PALLET_40x54,
      active: palletData.type === PalletType.PALLET_40x54,
      legend: 'Pallet',
    },
    {
      type: ButtonType.OUTLINE,
      children: PalletType.INDUSTRIE,
      active: palletData.type === PalletType.INDUSTRIE,
      legend: 'Industrie',
    },
    {
      type: ButtonType.OUTLINE,
      children: PalletType.EUROPALLET,
      active: palletData.type === PalletType.EUROPALLET,
      legend: 'Europallet',
    },
    {
      type: ButtonType.OUTLINE,
      children: PalletType.CUSTOM,
      active: palletData.type === PalletType.CUSTOM,
      className: 'col-span-2 sm:col-span-1',
    },
  ];

  const palletDataOnChange = (item: ButtonProps) => setPalletData(
    item.children as PalletType,
  );

  const unitOptions: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: UnitDimension.INCH,
      active: palletData.unitType === UnitDimension.INCH,
      className: styles.toggleButton2,
    },
    {
      type: ButtonType.PILL,
      children: UnitDimension.MM,
      active: palletData.unitType === UnitDimension.MM,
      className: styles.toggleButton1,
    },
  ];

  const unitDimensionsChange = (item: string) => setPalletUnit(
    item as UnitDimension,
  );

  const dimensionsSelector = (
    <Selector
      items={unitOptions}
      onChange={unitDimensionsChange}
      selected={palletData.unitType}
    />
  );

  const widthChange = (value: number) => setPalletWidth(value);
  const lengthChange = (value: number) => setPalletLength(value);
  const heightChange = (value: number) => setPalletHeight(value);

  const palletIconRef = React.createRef<HTMLElement>();
  const rightSideChildren = <PalletIcon className="max-w-full min-h-[60%]" ref={palletIconRef} />;

  const inputs = (
    <section>
      <div className={styles.toggleSection}>
        <Heading>Custom size</Heading>
      </div>
      <div className={styles.inputsBlock}>
        <Input
          id="width"
          label={t('home:width')}
          placeholder={dimensionsSelector}
          type="number"
          onChange={widthChange}
          value={String(palletData.width) || ''}
          error={widthError}
        />
        <Input
          id="length"
          label={t('home:length')}
          placeholder={dimensionsSelector}
          type="number"
          onChange={lengthChange}
          value={String(palletData.length) || ''}
          error={lengthError}
        />
        <Input
          className="col-span-2 sm:col-span-1"
          id="height"
          label={t('home:height')}
          placeholder={dimensionsSelector}
          type="number"
          onChange={heightChange}
          value={String(palletData.height) || ''}
          error={heightError}
        />
      </div>
    </section>
  );

  const message = t('palletHeightInfo', { inches: PALLET_MAX_HEIGHT_INCHES, mm: 152 });
  const widthSpan = palletData.width ? `${palletData.width} ${palletData.unitType}` : 'Width';
  const lengthSpan = palletData.length ? `${palletData.length} ${palletData.unitType}` : 'Length';
  const heightSpan = palletData.height ? `${palletData.height} ${palletData.unitType}` : 'Height';

  const widthClasses = palletData.type === PalletType.PALLET_40x48
  || palletData.type === PalletType.PALLET_40x54
    ? styles.dimensionLength : styles.dimensionWidth;
  const lengthClasses = palletData.type === PalletType.PALLET_40x48
  || palletData.type === PalletType.PALLET_40x54
    ? styles.dimensionWidth : styles.dimensionLength;

  useEffect(() => {
    const { current } = palletIconRef || {};
    if (current) {
      const onAnimateEnd = () => {
        if (current) {
          current.classList.remove('shine-animate');
        }
      };

      onAnimateEnd();
      current.classList.add('shine-animate');
      current.addEventListener('animationend', onAnimateEnd);
      return () => current.removeEventListener('animationend', onAnimateEnd);
    }

    return () => {
    };
  }, [palletIconRef, palletData]);

  return (
    <section className="md:flex justify-between md:gap-12 text-lg">
      <section className="user-input md:w-2/5">
        <section id="pallet-data">
          <Heading size={HeadingSize.H1}>
            {t('home:step2.title')}
          </Heading>
          <p>{t('home:step2.description')}</p>
          <Info message={message} />
          <Heading className="!mt-7">Pallet Type</Heading>
          <ToggleButtons
            className="grid grid-cols-2 sm:grid-cols-3 gap-4"
            items={palletTypes}
            onChange={palletDataOnChange}
          />
          {palletData.type === PalletType.CUSTOM && inputs}
        </section>
        {!isMobile && <NextPrevButtons nextButtonEnabled={isStep2Valid()} />}
      </section>
      <GrayContainer className="!my-14 md:!my-0 md:w-3/5 md:!py-44">
        {rightSideChildren}
        <div className={widthClasses}>
          {widthSpan}
        </div>
        <div className={lengthClasses}>
          {lengthSpan}
        </div>
        <div className={styles.dimensionHeight}>
          {heightSpan}
        </div>
      </GrayContainer>
      {isMobile && <NextPrevButtons nextButtonEnabled={isStep2Valid()} />}
    </section>
  );
};

export default Step2;
